import { attachOverlaySkinSnapshots, readSkinRevision } from "./overlaySkins";
import { parseCustomSkinReference } from "@/models/overlaySkin";
import { isValidTimeZone } from "@/models/zonedTime";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit as queryLimit,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  where,
} from "firebase/firestore";
import { db } from "@/firebase/app";
import { parseExternalDataSource, type LeagueExternalDataSource } from "@/models/externalSource";
import { parseTrackerDocument } from "@/models/gps";
import { parseOrganization } from "@/models/organization";
import {
  COURSE_PROFILE_TRACK_LIMIT,
  CUSTOM_LOWER_THIRD_ROW_LIMIT,
  OVERLAY_ELEMENT_TYPE_KEYS,
  OVERLAY_ELEMENT_TYPES,
  OVERLAY_INSTANCE_ID_PATTERN,
  PODIUM_SLOT_LIMIT,
  PYLON_DIVIDER_LIMIT,
  PYLON_ROW_LIMIT,
  SORT_FILTER_PYLON_ROW_LIMIT,
  START_LIST_ROW_LIMIT,
  podiumVisibleSlots,
  pylonAthleteRows,
  billboardInstanceFirestoreData,
  imageCanvasInstanceFirestoreData,
  webSourceInstanceFirestoreData,
  normalizeWebSourceURL,
  bugInstanceFirestoreData,
  bugReadinessIssue,
  compassInstanceFirestoreData,
  courseProfileInstanceFirestoreData,
  customLowerThirdInstanceFirestoreData,
  customWidgetInstanceFirestoreData,
  customWidgetSpecIssues,
  fullMapInstanceFirestoreData,
  gpsGroupInstanceFirestoreData,
  gpsPylonInstanceFirestoreData,
  sortFilterPylonInstanceFirestoreData,
  startListInstanceFirestoreData,
  tickerInstanceFirestoreData,
  advancedTimerInstanceFirestoreData,
  advancedTimerHasContent,
  newAdvancedTimerInstance,
  type OverlayAdvancedTimerInstance,
  tickerHasContent,
  newTickerInstance,
  TICKER_MESSAGE_LIMIT,
  TICKER_MESSAGE_MAX_LENGTH,
  type OverlayTickerInstance,
  gradeScopeInstanceFirestoreData,
  lowerThirdInstanceFirestoreData,
  lowerThirdReadinessIssue,
  miniMapInstanceFirestoreData,
  newBillboardInstance,
  newImageCanvasInstance,
  newWebSourceInstance,
  newBugInstance,
  newCompassInstance,
  newCourseProfileInstance,
  newCustomLowerThirdInstance,
  newCustomWidgetInstance,
  newFullMapInstance,
  newGpsGroupInstance,
  newGpsPylonInstance,
  newSortFilterPylonInstance,
  newStartListInstance,
  newGradeScopeInstance,
  newLowerThirdInstance,
  newMiniMapInstance,
  newPlayerCardInstance,
  newPodiumInstance,
  newPylonInstance,
  newScoreboardInstance,
  newSpeedometerInstance,
  newTimerInstance,
  overlayElementsFirestoreData,
  overlayInstanceCap,
  parseOverlayConfig,
  parseOverlayElements,
  playerCardInstanceFirestoreData,
  podiumInstanceFirestoreData,
  pylonInstanceFirestoreData,
  scoreboardInstanceFirestoreData,
  speedometerInstanceFirestoreData,
  timerElapsedMs,
  timerInstanceFirestoreData,
  type OverlayConfig,
  type OverlayBugInstance,
  type OverlayCustomWidgetInstance,
  type OverlayElementInstance,
  type OverlayElementType,
  type OverlayElements,
  type OverlayGpsPylonInstance,
  type OverlayLowerThirdPerson,
  type OverlayLowerThirdInstance,
  type OverlayPodiumInstance,
  type OverlayPylonInstance,
  type OverlayTimerInstance,
} from "@/models/overlayConfig";
import { fetchCountryResources } from "@/models/countryResource";
import { shortNameFromFullName } from "@/models/athleteUniversalFields";
import { parseAthleteValue, parseFieldSchema } from "@/models/schema";
import { personOptionsFromAthletes, personOptionsFromExternalRecords } from "@/features/overlays/lowerThirdPeople";
import {
  LEAGUE_ATHLETES_SOURCE_ID,
  gpsPylonWorkspaceSlot,
  sortFilterPylonWorkspaceSlot,
  startListWorkspaceSlot,
  lowerThirdWorkspaceSlot,
  playerCardWorkspaceSlot,
  podiumWorkspaceSlot,
  pylonWorkspaceSlot,
  selectOverlayPerson,
  selectOverlayPersonSource,
} from "./overlayPersonWorkspace";
import {
  getOverlayRuntimeState,
  overlayControlKey,
  setOverlayControl,
  type OverlayRuntimeState,
} from "./overlayRuntime";

export interface OverlayInputControl {
  type: OverlayElementType;
  instanceID: string;
}

export interface OverlayInputFieldGuide {
  control: string[];
  data: string[];
}

export type OverlayInputCandidateKind = "source" | "person" | "organization" | "gpsDevice";

export interface OverlayInputCandidate {
  kind: OverlayInputCandidateKind;
  handle: string;
  label: string;
  details: Record<string, string>;
}

export interface OverlayInputSnapshot {
  handle: string;
  type: OverlayElementType;
  instanceID: string;
  desiredVisible: boolean;
  control: Record<string, unknown>;
  data: Record<string, unknown>;
}

export interface OverlayResolvedInputPatches {
  control: Record<string, unknown>;
  data: Record<string, unknown>;
}

export interface OverlayInputMutationResult {
  config: OverlayConfig;
  control: OverlayInputControl;
  snapshot: OverlayInputSnapshot;
  fingerprint: string;
  takenOut: OverlayInputControl[];
}

export interface OverlayInputVisibilityResult {
  config: OverlayConfig;
  runtimeState: OverlayRuntimeState;
  noOp: boolean;
  takenOut: OverlayInputControl[];
}

export interface OverlayApplicationServicing {
  listOverlays(leagueID: string): Promise<OverlayConfig[]>;
  loadOverlay(overlayID: string, leagueID: string): Promise<OverlayConfig>;
  runtimeState(overlayID: string, leagueID: string): Promise<OverlayRuntimeState>;
  inputCandidates(
    overlayID: string,
    leagueID: string,
    control: OverlayInputControl,
    candidateQuery?: string,
    candidateLimit?: number,
  ): Promise<OverlayInputCandidate[]>;
  resolveToolPatches(
    overlayID: string,
    leagueID: string,
    control: OverlayInputControl,
    controlPatch: Record<string, unknown>,
    dataPatch: Record<string, unknown>,
  ): Promise<OverlayResolvedInputPatches>;
  setInputVisibility(
    overlayID: string,
    leagueID: string,
    control: OverlayInputControl,
    visible: boolean,
  ): Promise<OverlayInputVisibilityResult>;
  updateInput(
    overlayID: string,
    leagueID: string,
    control: OverlayInputControl,
    controlPatch: Record<string, unknown>,
    dataPatch: Record<string, unknown>,
    expectedFingerprint?: string,
  ): Promise<OverlayInputMutationResult>;
  addInput(
    overlayID: string,
    leagueID: string,
    type: OverlayElementType,
    instanceID: string,
    controlPatch: Record<string, unknown>,
    dataPatch: Record<string, unknown>,
    expectedFingerprint?: string,
  ): Promise<OverlayInputMutationResult>;
}

export class OverlayApplicationError extends Error {
  constructor(
    readonly kind:
      | "overlayUnavailable"
      | "workspaceMismatch"
      | "instanceUnavailable"
      | "staleApproval"
      | "invalidInstanceID"
      | "invalidPatch"
      | "contentGate",
    message?: string,
  ) {
    super(message ?? overlayApplicationErrorMessage(kind));
    this.name = "OverlayApplicationError";
  }
}

function overlayApplicationErrorMessage(kind: OverlayApplicationError["kind"]): string {
  switch (kind) {
    case "overlayUnavailable": return "Overlay is no longer available.";
    case "workspaceMismatch": return "That overlay does not belong to the active league workspace.";
    case "instanceUnavailable": return "Overlay input is no longer available.";
    case "staleApproval": return "The overlay changed after this action was prepared. Prepare the action again.";
    case "invalidInstanceID": return "Input ids must contain 1–40 letters, numbers, underscores, or hyphens.";
    case "invalidPatch": return "Invalid overlay input values.";
    case "contentGate": return "The overlay input does not have enough content to go live.";
  }
}

const COMMON_CONTROL_FIELDS = ["rootScale", "name", "transitionDurationMs", "avoidCollisions", "region", "offsets"];
const TRANSIENT_FIELDS = new Set(["action", "sourceHandle", "personHandle", "gpsDeviceHandle", "organizationHandle"]);

export const OverlayInputParameterCodec = {
  supportedFields(type: OverlayElementType): OverlayInputFieldGuide {
    switch (type) {
      case "gps":
        return guide([...COMMON_CONTROL_FIELDS, "category", "showChase", "showChaseValue", "leadLabel", "chaseLabel", "leadScale", "chaseScale", "leadWidth", "chaseWidth", "lockChaseWidth", "darkModeOverride", "skin", "customSkin", "material", "materialTextureOpacities"], ["showDistanceRemaining", "showSpeed", "showGrade", "showPace", "speedUnit"]);
      case "timer":
        return guide([...COMMON_CONTROL_FIELDS, "label", "direction", "durationMs", "darkModeOverride", "skin", "customSkin", "material", "materialTextureOpacities"], ["action", "offsetMs"]);
      case "pylon":
        return guide([...COMMON_CONTROL_FIELDS, "label", "useFlag", "useBib", "useShortName", "anchorBottom", "widthMode", "shrinkTextToFit", "darkModeOverride", "maxWidth", "maxHeight", "scale", "skin", "customSkin", "material", "materialTextureOpacities"], ["showRank", "sourceID", "sourceHandle", "rows"]);
      case "gpsPylon":
        // Live list must match mergeLiveInstance's gpsPylon branch in OverlayDesigner.tsx.
        return guide([...COMMON_CONTROL_FIELDS, "label", "category", "useFlag", "useBib", "useShortName", "anchorBottom", "widthMode", "shrinkTextToFit", "darkModeOverride", "maxWidth", "maxHeight", "scale", "skin", "customSkin", "material", "materialTextureOpacities"], ["showRank", "headerReadout", "speedUnit", "sourceID", "sourceHandle", "rows"]);
      case "sortFilterPylon":
        // Live list must match mergeLiveInstance's sortFilterPylon branch in OverlayDesigner.tsx.
        return guide([...COMMON_CONTROL_FIELDS, "label", "useFlag", "useBib", "useShortName", "anchorBottom", "widthMode", "shrinkTextToFit", "darkModeOverride", "maxWidth", "maxHeight", "scale", "skin", "customSkin", "material", "materialTextureOpacities"], ["showRank", "showSortValue", "sourceID", "sourceHandle", "sortFieldKey", "sortDirection", "filterFieldKey", "filterValue", "rowCount", "pageIndex", "rows"]);
      case "lowerThird":
        return guide([...COMMON_CONTROL_FIELDS, "fitLayoutReferenceLeft", "useFlag", "useBib", "showExtraLabel", "useShortName", "widthMode", "shrinkTextToFit", "darkModeOverride", "maxWidth", "maxHeight", "scale", "logo", "skin", "customSkin", "material", "materialTextureOpacities"], ["mode", "rows", "selectedRowID", "sourceID", "sourceHandle", "personHandle", "recordKey", "person"]);
      case "customLowerThird":
        return guide([...COMMON_CONTROL_FIELDS, "fitLayoutReferenceLeft", "widthMode", "shrinkTextToFit", "darkModeOverride", "maxWidth", "maxHeight", "scale", "logo", "skin", "customSkin", "material", "materialTextureOpacities"], ["selectedRowID", "rows"]);
      case "playerCard":
        return guide([...COMMON_CONTROL_FIELDS, "useFlag", "useBib", "showExtraLabel", "useShortName", "photoFit", "photoPosition", "photoBackground", "photoBackgroundOpacity", "darkModeOverride", "maxWidth", "maxHeight", "scale", "skin", "customSkin", "material", "materialTextureOpacities"], ["sourceID", "sourceHandle", "personHandle", "recordKey", "person"]);
      case "scoreboard":
        return guide([...COMMON_CONTROL_FIELDS, "team1Name", "team2Name", "team1Color", "team2Color", "darkModeOverride", "skin", "customSkin", "material", "materialTextureOpacities", "organizationHandle"], ["score1", "score2"]);
      case "miniMap":
        return guide([...COMMON_CONTROL_FIELDS, "zoomSpan", "diameter", "ringColor", "bibText", "labelText", "organizationHandle"], ["deviceID", "gpsDeviceHandle", "showSpeed", "speedUnit"]);
      case "fullMap":
        // Full-canvas element: no region/offsets (fixed nominal region "full").
        return guide(["name", "transitionDurationMs", "avoidCollisions", "zoomSpan", "mapType", "colorScheme", "tintColor", "rotation", "courseLineColor", "organizationHandle"], ["deviceID", "gpsDeviceHandle", "framing"]);
      case "courseProfile":
        return guide([...COMMON_CONTROL_FIELDS, "width", "height", "baseColor", "baseOpacity", "organizationHandle"], ["tracks"]);
      case "speedometer":
        return guide([...COMMON_CONTROL_FIELDS, "diameter", "maxSpeed", "ringColor", "labelText", "organizationHandle"], ["deviceID", "gpsDeviceHandle", "speedUnit", "displayMode"]);
      case "gradeScope":
        return guide([...COMMON_CONTROL_FIELDS, "diameter", "tiltGain", "ringColor", "labelText", "organizationHandle"], ["deviceID", "gpsDeviceHandle"]);
      case "customWidget":
        // Spec and values are live fields (immediate writes, like scores) —
        // never draft styling, so an operator's dirty draft can't clobber them.
        return guide([...COMMON_CONTROL_FIELDS], ["spec", "values"]);
      case "bug":
        return guide([...COMMON_CONTROL_FIELDS, "rotationIntervalSeconds"], ["mode", "sourceID", "logoField", "accentField", "selectedRowIndices", "dataRows", "rows"]);
      case "compass":
        return guide([...COMMON_CONTROL_FIELDS, "diameter", "ringColor", "labelText", "organizationHandle"], ["deviceID", "gpsDeviceHandle", "showReadout"]);
      case "billboard":
        return guide([...COMMON_CONTROL_FIELDS, "mode", "rotationIntervalSeconds", "activeRowId", "scale", "skin", "customSkin", "material", "materialTextureOpacities"], ["rows"]);
      case "webSource":
        return guide(["rootScale", "name", "transitionDurationMs", "avoidCollisions", "offsets", "canvasWidth", "canvasHeight", "scale", "unloadWhenHidden", "reloadToken", "organizationHandle"], ["url"]);
      case "imageCanvas":
        // Fixed region "canvas" (fullMap precedent) — no region in the guide. Everything is draft
        // styling (control list must match mergeLiveInstance's imageCanvas branch in OverlayDesigner.tsx);
        // the image URL is the Data-tab field (billboard rows precedent).
        return guide(["rootScale", "name", "transitionDurationMs", "avoidCollisions", "offsets", "canvasWidth", "canvasHeight", "background", "backgroundOpacity", "cornerRadius", "photoFit", "photoPosition", "scale", "organizationHandle"], ["url"]);
      case "podium":
        // Depth and slots are live fields (operator decisions applied immediately, like pylon rows).
        return guide([...COMMON_CONTROL_FIELDS, "label", "showPlace", "useFlag", "useBib", "useShortName", "shrinkTextToFit", "darkModeOverride", "barMaxWidth", "maxWidth", "maxHeight", "scale", "skin", "customSkin", "material", "materialTextureOpacities"], ["depth", "sourceID", "sourceHandle", "slots"]);
      case "startList":
        // Fixed region "center" (billboard precedent) — no region in the guide. Live list must match
        // mergeLiveInstance's startList branch in OverlayDesigner.tsx (rows derive from the source).
        return guide(["rootScale", "name", "transitionDurationMs", "avoidCollisions", "offsets", "label", "logoMode", "customLogoURL", "customLogoDarkURL", "logoHeight", "useFlag", "useBib", "useShortName", "shrinkTextToFit", "darkModeOverride", "maxWidth", "maxHeight", "scale", "skin", "customSkin", "material", "materialTextureOpacities"], ["sourceID", "sourceHandle", "sortFieldKey", "sortDirection", "filterFieldKey", "filterValue", "columns", "rowsPerColumn", "pageIndex", "rows"]);
      case "ticker":
        // Not skinnable (dark mode + material only). Messages are live (immediate writes, editable
        // on air) — list must match mergeLiveInstance's ticker branch in OverlayDesigner.tsx.
        return guide([...COMMON_CONTROL_FIELDS, "divider", "dividerImageURL", "direction", "speed", "barWidth", "fontSize", "darkModeOverride", "material", "materialTextureOpacities"], ["messages"]);
      case "advancedTimer":
        // Clock-derived (no start/pause) — everything is draft styling, matching mergeLiveInstance's
        // base-only advancedTimer branch in OverlayDesigner.tsx. targetAt is an ISO-8601 instant.
        return guide([...COMMON_CONTROL_FIELDS, "label", "mode", "targetAt", "timeZone", "darkModeOverride", "skin", "customSkin", "material", "materialTextureOpacities"], []);
    }
  },

  validatedInstanceID(value: string): string {
    if (!OVERLAY_INSTANCE_ID_PATTERN.test(value)) throw new OverlayApplicationError("invalidInstanceID");
    return value;
  },

  controls(config: OverlayConfig): OverlayInputControl[] {
    return OVERLAY_ELEMENT_TYPES.flatMap((type) => elementList(config, type).map((instance) => ({ type, instanceID: instance.id })));
  },

  contains(control: OverlayInputControl, config: OverlayConfig): boolean {
    return instanceForControl(config, control) !== undefined;
  },

  desiredVisibility(control: OverlayInputControl, config: OverlayConfig): boolean {
    return instanceForControl(config, control)?.visible ?? false;
  },

  snapshot(control: OverlayInputControl, config: OverlayConfig): OverlayInputSnapshot {
    const instance = instanceForControl(config, control);
    if (!instance) throw new OverlayApplicationError("instanceUnavailable");
    const fields = this.supportedFields(control.type);
    const record = instance as unknown as Record<string, unknown>;
    return {
      handle: overlayControlKey(control.type, control.instanceID),
      type: control.type,
      instanceID: control.instanceID,
      desiredVisible: instance.visible,
      control: selectedValues(record, fields.control.filter((field) => !TRANSIENT_FIELDS.has(field))),
      data: selectedValues(record, fields.data.filter((field) => !TRANSIENT_FIELDS.has(field))),
    };
  },

  async fingerprint(control: OverlayInputControl, config: OverlayConfig): Promise<string> {
    return fingerprint(this.snapshot(control, config));
  },

  async visibilityFingerprint(control: OverlayInputControl, config: OverlayConfig): Promise<string> {
    return fingerprint({
      type: control.type,
      instanceID: control.instanceID,
      exists: this.contains(control, config),
      desiredVisible: this.desiredVisibility(control, config),
    });
  },

  async targetedFingerprint(
    control: OverlayInputControl,
    controlFields: Iterable<string>,
    dataFields: Iterable<string>,
    config: OverlayConfig,
  ): Promise<string> {
    const instance = instanceForControl(config, control);
    if (!instance) return fingerprint({ type: control.type, instanceID: control.instanceID, exists: false });
    const record = instance as unknown as Record<string, unknown>;
    const values: Record<string, unknown> = { type: control.type, instanceID: control.instanceID, exists: true };
    for (const key of [...controlFields].sort()) values[`control.${key}`] = record[key] ?? null;
    const dataKeys = [...dataFields].sort();
    for (const key of dataKeys) if (key !== "action") values[`data.${key}`] = record[key] ?? null;
    if (dataKeys.includes("action")) {
      for (const key of ["running", "startedAt", "offsetMs", "durationMs"]) values[`data.${key}`] = record[key] ?? null;
    }
    return fingerprint(values);
  },

  async creationFingerprint(control: OverlayInputControl, config: OverlayConfig): Promise<string> {
    return fingerprint({ type: control.type, instanceID: control.instanceID, exists: this.contains(control, config) });
  },

  predictedCollisionTakeOuts(control: OverlayInputControl, visible: boolean, config: OverlayConfig): OverlayInputControl[] {
    const incoming = instanceForControl(config, control);
    if (!visible || !incoming || !incoming.avoidCollisions) return [];
    return this.controls(config).filter((candidate) => {
      if (candidate.type === control.type && candidate.instanceID === control.instanceID) return false;
      const instance = instanceForControl(config, candidate);
      if (!instance?.visible || !instance.avoidCollisions) return false;
      const sameType = candidate.type === control.type && instance.region === incoming.region;
      const crossType = (control.type === "gps" && candidate.type === "scoreboard")
        || (control.type === "scoreboard" && candidate.type === "gps");
      // Timers and advanced timers share the same top/bottom chip slots.
      const sameSlotTimer = instance.region === incoming.region
        && ((control.type === "timer" && candidate.type === "advancedTimer")
          || (control.type === "advancedTimer" && candidate.type === "timer"));
      return sameType || crossType || sameSlotTimer;
    });
  },

  applyPredictedCollisionTakeOuts(control: OverlayInputControl, visible: boolean, config: OverlayConfig): { config: OverlayConfig; takenOut: OverlayInputControl[] } {
    const takenOut = this.predictedCollisionTakeOuts(control, visible, config);
    let next = config;
    for (const candidate of takenOut) {
      next = replaceInstance(next, candidate, (instance) => ({ ...instance, visible: false } as OverlayElementInstance));
    }
    return { config: next, takenOut };
  },

  apply(
    controlPatch: Record<string, unknown>,
    dataPatch: Record<string, unknown>,
    control: OverlayInputControl,
    config: OverlayConfig,
    now = new Date(),
  ): OverlayConfig {
    validatePatch(controlPatch, this.supportedFields(control.type).control, "control");
    validatePatch(dataPatch, this.supportedFields(control.type).data, "data");
    if (control.type === "customWidget") validateCustomWidgetPatch(dataPatch);
    if (control.type === "ticker" && dataPatch.messages !== undefined) validateTickerMessagesPatch(dataPatch.messages);
    if (control.type === "advancedTimer") controlPatch = advancedTimerControlPatch(controlPatch);
    if (control.type === "webSource" && dataPatch.url != null
      && (typeof dataPatch.url !== "string" || (dataPatch.url.trim() !== "" && !normalizeWebSourceURL(dataPatch.url)))) {
      throw new OverlayApplicationError("invalidPatch", "Web Source URL must be a valid HTTPS URL without embedded credentials (maximum 8192 characters).");
    }
    const current = instanceForControl(config, control);
    if (!current) throw new OverlayApplicationError("instanceUnavailable");
    let raw = deepMerge(instanceFirestoreData(control.type, current), controlPatch);
    const { action, ...storedData } = dataPatch;
    raw = deepMerge(raw, storedData);
    if (Object.hasOwn(controlPatch, "skin") && !Object.hasOwn(controlPatch, "customSkin")) delete raw.customSkin;
    if (controlPatch.customSkin != null && !parseCustomSkinReference(controlPatch.customSkin)) throw new OverlayApplicationError("invalidPatch", "Invalid custom skin reference.");
    if (control.type === "bug") {
      const previous = current as OverlayBugInstance;
      const sourceChanged = Object.hasOwn(storedData, "sourceID") && storedData.sourceID !== previous.sourceID;
      if (sourceChanged) {
        for (const field of ["logoField", "accentField", "selectedRowIndices"] as const) {
          if (!Object.hasOwn(storedData, field)) raw[field] = field === "selectedRowIndices" ? [] : null;
        }
      }
      const bindingChanged = sourceChanged || ["logoField", "accentField", "selectedRowIndices"].some((field) =>
        Object.hasOwn(storedData, field) && JSON.stringify(storedData[field]) !== JSON.stringify(previous[field as keyof OverlayBugInstance]));
      if (bindingChanged && !Object.hasOwn(storedData, "dataRows")) raw.dataRows = [];
    }
    raw.id = control.instanceID;
    raw.visible = current.visible;
    let parsed = parseInstance(control.type, raw);
    if (control.type === "lowerThird" && current.visible) {
      const before = current as OverlayLowerThirdInstance;
      const after = parsed as OverlayLowerThirdInstance;
      if (before.mode !== after.mode || before.selectedRowID !== after.selectedRowID) {
        throw new OverlayApplicationError("invalidPatch", "Take this lower third OUT before changing its mode or selected manual row.");
      }
      if (before.mode === "manual" && before.selectedRowID && !after.rows.some((row) => row.id === before.selectedRowID)) {
        throw new OverlayApplicationError("invalidPatch", "Take this lower third OUT before deleting its selected manual row.");
      }
    }
    if (control.type === "customWidget" && dataPatch.values !== undefined) {
      validateCustomWidgetValueKeys(dataPatch.values, parsed as OverlayCustomWidgetInstance);
    }
    if (action !== undefined) {
      if (control.type !== "timer" || typeof action !== "string" || !["start", "pause", "reset"].includes(action.toLowerCase())) {
        throw new OverlayApplicationError("invalidPatch", "Timer action must be start, pause, or reset.");
      }
      parsed = transitionTimer(parsed as OverlayTimerInstance, action.toLowerCase() as "start" | "pause" | "reset", now);
    }
    return replaceInstance(config, control, () => parsed);
  },

  add(
    type: OverlayElementType,
    instanceID: string,
    controlPatch: Record<string, unknown>,
    dataPatch: Record<string, unknown>,
    config: OverlayConfig,
    now = new Date(),
  ): OverlayConfig {
    this.validatedInstanceID(instanceID);
    if (elementList(config, type).length >= overlayInstanceCap(type)) {
      throw new OverlayApplicationError("invalidPatch", `The ${type} input limit has been reached.`);
    }
    if (this.controls(config).some((control) => control.instanceID === instanceID)) {
      throw new OverlayApplicationError("invalidPatch", "Input id is already in use.");
    }
    const name = `${inputTypeLabel(type)} ${elementList(config, type).length + 1}`;
    let next = withElements(config, type, [...elementList(config, type), newInstance(type, instanceID, name)]);
    next = this.apply(controlPatch, dataPatch, { type, instanceID }, next, now);
    return replaceInstance(next, { type, instanceID }, (instance) => ({ ...instance, visible: false } as OverlayElementInstance));
  },
};

export function makeOverlayHandle(id: string): string {
  return `overlay:${id}`;
}

export function makeOverlayInputHandle(control: OverlayInputControl): string {
  return `input:${control.type}:${control.instanceID}`;
}

export function parseOverlayInputHandle(handle: string): OverlayInputControl {
  const match = /^input:([^:]+):(.+)$/.exec(handle);
  const type = match?.[1] as OverlayElementType | undefined;
  const instanceID = match?.[2];
  if (!type || !OVERLAY_ELEMENT_TYPES.includes(type) || !instanceID || !OVERLAY_INSTANCE_ID_PATTERN.test(instanceID)) {
    throw new OverlayApplicationError("invalidPatch", `Expected an input:<type>:<instanceID> handle, got '${handle}'.`);
  }
  return { type, instanceID };
}

export function contentGateError(control: OverlayInputControl, config: OverlayConfig): string | undefined {
  const instance = instanceForControl(config, control);
  if (!instance) throw new OverlayApplicationError("instanceUnavailable");
  switch (control.type) {
    case "pylon":
      return pylonAthleteRows((instance as OverlayPylonInstance).rows).some((row) => row.person["full-name"]?.trim())
        ? undefined
        : "Add at least one athlete to the pylon before taking it live.";
    case "gpsPylon":
      return pylonAthleteRows((instance as OverlayGpsPylonInstance).rows).some((row) => row.person["full-name"]?.trim())
        ? undefined
        : "Add at least one athlete to the GPS pylon before taking it live.";
    case "sortFilterPylon":
      return pylonAthleteRows((instance as OverlayPylonInstance).rows).some((row) => row.person["full-name"]?.trim())
        ? undefined
        : "Bind a source with at least one matching record before taking this pylon live.";
    case "startList":
      return (instance as { rows: { person: OverlayLowerThirdPerson }[] }).rows.some((row) => row.person["full-name"]?.trim())
        ? undefined
        : "Bind a source with at least one matching record before taking the start list live.";
    case "lowerThird":
      return lowerThirdReadinessIssue(instance as OverlayLowerThirdInstance);
    case "playerCard":
      return (instance as { person: OverlayLowerThirdPerson | null }).person?.["full-name"]?.trim()
        ? undefined
        : "Select a person before taking this player card live.";
    case "customLowerThird": {
      const custom = instance as { selectedRowID?: string; rows: { id: string; name: string }[] };
      return custom.rows.find((row) => row.id === custom.selectedRowID)?.name.trim()
        ? undefined
        : "Select a non-empty custom lower-third row before taking it live.";
    }
    case "customWidget":
      return (instance as OverlayCustomWidgetInstance).spec.layers.length > 0
        ? undefined
        : "Design the widget (add at least one layer) before taking it live.";
    case "bug":
      return bugReadinessIssue(instance as OverlayBugInstance);
    case "billboard":
      return (instance as { rows: { url: string; message: string }[] }).rows.some((row) => row.url.trim() || row.message.trim())
        ? undefined
        : "Add a logo URL or message to a slate before taking the billboard live.";
    case "podium":
      return podiumVisibleSlots(instance as OverlayPodiumInstance).some((slot) => slot.person["full-name"]?.trim())
        ? undefined
        : "Fill at least one podium place before taking the podium live.";
    case "webSource":
      return normalizeWebSourceURL((instance as { url: string }).url)
        ? undefined
        : "Add a valid HTTPS webpage URL before taking the web source live.";
    case "imageCanvas":
      return (instance as { url: string }).url.trim()
        ? undefined
        : "Add an image URL before taking the image canvas live.";
    case "ticker":
      return tickerHasContent(instance as OverlayTickerInstance)
        ? undefined
        : "Add at least one ticker message before taking it live.";
    case "advancedTimer":
      return advancedTimerHasContent(instance as OverlayAdvancedTimerInstance)
        ? undefined
        : "Set a target date and time before taking the advanced timer live.";
    default:
      return undefined;
  }
}

/** Updates publish stored data directly, even while the input is Out. Draft creation stays permissive. */
export function inputUpdateGateError(control: OverlayInputControl, config: OverlayConfig, dataPatch: Record<string, unknown>): string | undefined {
  const instance = instanceForControl(config, control);
  const publishingBugData = control.type === "bug" && (instance as OverlayBugInstance | undefined)?.mode === "data"
    && Object.keys(dataPatch).some((key) => ["mode", "sourceID", "logoField", "accentField", "selectedRowIndices", "dataRows"].includes(key));
  return publishingBugData || OverlayInputParameterCodec.desiredVisibility(control, config) ? contentGateError(control, config) : undefined;
}

export class OverlayApplicationService implements OverlayApplicationServicing {
  async listOverlays(leagueID: string): Promise<OverlayConfig[]> {
    const snapshots = await getDocs(query(collection(db, "overlayConfigs"), where("leagueID", "==", leagueID)));
    return snapshots.docs
      .map((entry) => parseOverlayConfig(entry.id, entry.data()))
      .filter((config): config is OverlayConfig => config !== null)
      .sort((first, second) => first.name.localeCompare(second.name) || first.id.localeCompare(second.id));
  }

  async loadOverlay(overlayID: string, leagueID: string): Promise<OverlayConfig> {
    const snapshot = await getDoc(doc(db, "overlayConfigs", overlayID));
    const config = snapshot.exists() ? parseOverlayConfig(snapshot.id, snapshot.data()) : null;
    if (!config) throw new OverlayApplicationError("overlayUnavailable");
    if (config.leagueID !== leagueID) throw new OverlayApplicationError("workspaceMismatch");
    return config;
  }

  async runtimeState(overlayID: string, leagueID: string): Promise<OverlayRuntimeState> {
    await this.loadOverlay(overlayID, leagueID);
    return getOverlayRuntimeState(overlayID);
  }

  async inputCandidates(
    overlayID: string,
    leagueID: string,
    control: OverlayInputControl,
    candidateQuery = "",
    candidateLimit = 25,
  ): Promise<OverlayInputCandidate[]> {
    const config = await this.loadOverlay(overlayID, leagueID);
    if (!OverlayInputParameterCodec.contains(control, config)) throw new OverlayApplicationError("instanceUnavailable");
    const sources = await this.loadSources(leagueID);
    const candidates: OverlayInputCandidate[] = sources.map((source) => ({
      kind: "source",
      handle: `source:${source.id}`,
      label: source.name,
      details: { sourceID: source.id, sourceKind: source.sourceKind },
    }));
    candidates.push({
      kind: "source",
      handle: `source:${LEAGUE_ATHLETES_SOURCE_ID}`,
      label: "League athletes",
      details: { sourceID: LEAGUE_ATHLETES_SOURCE_ID, sourceKind: "league" },
    });

    const athletes = await getDocs(query(collection(db, "leagueAthleteValues"), where("leagueID", "==", leagueID), queryLimit(200)));
    for (const entry of athletes.docs) {
      const athlete = parseAthleteValue(entry.id, entry.data());
      if (!athlete || athlete.status !== "active") continue;
      candidates.push({
        kind: "person",
        handle: `person:${LEAGUE_ATHLETES_SOURCE_ID}:${athlete.id}`,
        label: athlete.name,
        details: { sourceID: LEAGUE_ATHLETES_SOURCE_ID, recordKey: athlete.id },
      });
    }

    for (const source of sources) {
      const options = await this.cachedPersonOptions(overlayID, source, 200, false);
      for (const option of options) {
        candidates.push({
          kind: "person",
          handle: `person:${source.id}:${option.recordKey}`,
          label: option.label,
          details: { sourceID: source.id, recordKey: option.recordKey },
        });
      }
    }

    const organizations = await getDocs(query(collection(db, "organizations"), where("leagueID", "==", leagueID), queryLimit(100)));
    for (const entry of organizations.docs) {
      const organization = parseOrganization(entry.id, entry.data());
      if (!organization) continue;
      candidates.push({ kind: "organization", handle: `organization:${organization.id}`, label: organization.name, details: { organizationID: organization.id } });
    }

    const devices = await getDocs(query(collection(db, "leagueCompetitionInstances", config.competitionInstanceID, "gpsData"), queryLimit(100)));
    for (const entry of devices.docs) {
      const device = parseTrackerDocument(entry.id, entry.data());
      if (!device) continue;
      candidates.push({
        kind: "gpsDevice",
        handle: `gpsDevice:${device.id}`,
        label: device.displayName ?? device.gpsAlias ?? device.deviceName ?? device.id,
        details: { deviceID: device.id, competitionInstanceID: device.competitionInstanceID },
      });
    }

    const normalizedQuery = candidateQuery.trim().toLowerCase();
    return candidates
      .filter((candidate) => !normalizedQuery || candidate.label.toLowerCase().includes(normalizedQuery) || candidate.handle.toLowerCase().includes(normalizedQuery))
      .sort((first, second) => first.kind.localeCompare(second.kind) || first.label.localeCompare(second.label) || first.handle.localeCompare(second.handle))
      .slice(0, Math.min(50, Math.max(1, Math.trunc(candidateLimit))));
  }

  async resolveToolPatches(
    overlayID: string,
    leagueID: string,
    control: OverlayInputControl,
    controlPatch: Record<string, unknown>,
    dataPatch: Record<string, unknown>,
  ): Promise<OverlayResolvedInputPatches> {
    const config = await this.loadOverlay(overlayID, leagueID);
    const resolved = await this.resolveCandidateHandles(overlayID, leagueID, control, controlPatch, dataPatch, config);
    if (OverlayInputParameterCodec.contains(control, config)) {
      OverlayInputParameterCodec.apply(resolved.control, resolved.data, control, config);
    } else {
      OverlayInputParameterCodec.add(control.type, control.instanceID, resolved.control, resolved.data, config);
    }
    return resolved;
  }

  async setInputVisibility(
    overlayID: string,
    leagueID: string,
    control: OverlayInputControl,
    visible: boolean,
  ): Promise<OverlayInputVisibilityResult> {
    const config = await this.loadOverlay(overlayID, leagueID);
    if (!OverlayInputParameterCodec.contains(control, config)) throw new OverlayApplicationError("instanceUnavailable");
    const gate = visible ? contentGateError(control, config) : undefined;
    if (gate) throw new OverlayApplicationError("contentGate", gate);
    // One round trip: the server reads the pre-write state inside its own
    // transaction (the client's cached doc can lag the listener by seconds),
    // skips the write when nothing changes, and reports both. Raw (ungated)
    // state throughout: no-ops and take-outs describe individual flags.
    const key = overlayControlKey(control.type, control.instanceID);
    const { previousRawControls, changed, ...after } = await setOverlayControl(overlayID, key, visible);
    const beforeControls = previousRawControls ?? {};
    const afterControls = after.rawControls ?? after.controls;
    const noOp = changed !== undefined
      ? !changed
      : previousRawControls !== undefined && (beforeControls[key] ?? false) === visible;
    const takenOut = OverlayInputParameterCodec.controls(config).filter((candidate) => {
      const candidateKey = overlayControlKey(candidate.type, candidate.instanceID);
      return candidate.type !== control.type || candidate.instanceID !== control.instanceID
        ? beforeControls[candidateKey] === true && afterControls[candidateKey] !== true
        : false;
    });
    return { config: applyingRuntimeControls(config, after), runtimeState: after, noOp, takenOut };
  }

  async updateInput(
    overlayID: string,
    leagueID: string,
    control: OverlayInputControl,
    controlPatch: Record<string, unknown>,
    dataPatch: Record<string, unknown>,
    expectedFingerprint?: string,
  ): Promise<OverlayInputMutationResult> {
    const preflight = await this.loadOverlay(overlayID, leagueID);
    if (!OverlayInputParameterCodec.contains(control, preflight)) throw new OverlayApplicationError("instanceUnavailable");
    const resolved = await this.resolveCandidateHandles(overlayID, leagueID, control, controlPatch, dataPatch, preflight);
    if (expectedFingerprint) {
      const current = await OverlayInputParameterCodec.targetedFingerprint(control, Object.keys(resolved.control), Object.keys(resolved.data), preflight);
      if (current !== expectedFingerprint) throw new OverlayApplicationError("staleApproval");
    }
    const routedKeys = personWorkspaceDataKeys(control, resolved.data);
    if (routedKeys.size > 0 && OverlayInputParameterCodec.desiredVisibility(control, preflight)) {
      throw new OverlayApplicationError("invalidPatch", `Take this ${control.type === "pylon" ? "pylon" : control.type === "gpsPylon" ? "GPS pylon" : control.type === "podium" ? "podium" : "lower third"} OUT before changing its source or person.`);
    }
    const validated = OverlayInputParameterCodec.apply(resolved.control, resolved.data, control, preflight);
    const gate = inputUpdateGateError(control, validated, resolved.data);
    if (gate) throw new OverlayApplicationError("contentGate", gate);
    if (resolved.control.customSkin) await attachOverlaySkinSnapshots(validated);
    await routePersonWorkspaceChanges(overlayID, control, resolved.data);
    const localData = Object.fromEntries(Object.entries(resolved.data).filter(([key]) => !routedKeys.has(key)));
    if (Object.keys(resolved.control).length === 0 && Object.keys(localData).length === 0) {
      const config = await this.loadOverlay(overlayID, leagueID);
      const snapshot = OverlayInputParameterCodec.snapshot(control, config);
      return { config, control, snapshot, fingerprint: await fingerprint(snapshot), takenOut: [] };
    }
    const localExpected = await OverlayInputParameterCodec.targetedFingerprint(control, Object.keys(resolved.control), Object.keys(localData), preflight);
    let takenOut: OverlayInputControl[] = [];
    const config = await this.mutate(
      overlayID,
      leagueID,
      localExpected,
      (current) => OverlayInputParameterCodec.targetedFingerprint(control, Object.keys(resolved.control), Object.keys(localData), current),
      (current) => {
        let next = OverlayInputParameterCodec.apply(resolved.control, localData, control, current);
        const nextGate = inputUpdateGateError(control, next, localData);
        if (nextGate) throw new OverlayApplicationError("contentGate", nextGate);
        if (Object.hasOwn(resolved.control, "region")) {
          const collision = OverlayInputParameterCodec.applyPredictedCollisionTakeOuts(control, true, next);
          if (!OverlayInputParameterCodec.desiredVisibility(control, next)) collision.takenOut.length = 0;
          next = collision.takenOut.length > 0 ? collision.config : next;
          takenOut = collision.takenOut;
        }
        return next;
      },
    );
    const snapshot = OverlayInputParameterCodec.snapshot(control, config);
    return { config, control, snapshot, fingerprint: await fingerprint(snapshot), takenOut };
  }

  async addInput(
    overlayID: string,
    leagueID: string,
    type: OverlayElementType,
    instanceID: string,
    controlPatch: Record<string, unknown>,
    dataPatch: Record<string, unknown>,
    expectedFingerprint?: string,
  ): Promise<OverlayInputMutationResult> {
    const control = { type, instanceID: OverlayInputParameterCodec.validatedInstanceID(instanceID) };
    const preflight = await this.loadOverlay(overlayID, leagueID);
    if (expectedFingerprint && await OverlayInputParameterCodec.creationFingerprint(control, preflight) !== expectedFingerprint) {
      throw new OverlayApplicationError("staleApproval");
    }
    const resolved = await this.resolveCandidateHandles(overlayID, leagueID, control, controlPatch, dataPatch, preflight);
    const validated = OverlayInputParameterCodec.add(type, instanceID, resolved.control, resolved.data, preflight);
    if (resolved.control.customSkin) await attachOverlaySkinSnapshots(validated);
    const config = await this.mutate(
      overlayID,
      leagueID,
      expectedFingerprint,
      (current) => OverlayInputParameterCodec.creationFingerprint(control, current),
      (current) => OverlayInputParameterCodec.add(type, instanceID, resolved.control, resolved.data, current),
    );
    await routePersonWorkspaceChanges(overlayID, control, resolved.data);
    const fresh = personWorkspaceDataKeys(control, resolved.data).size > 0
      ? await this.loadOverlay(overlayID, leagueID)
      : config;
    const snapshot = OverlayInputParameterCodec.snapshot(control, fresh);
    return { config: fresh, control, snapshot, fingerprint: await fingerprint(snapshot), takenOut: [] };
  }

  private async mutate(
    overlayID: string,
    leagueID: string,
    expectedFingerprint: string | undefined,
    currentFingerprint: (config: OverlayConfig) => Promise<string>,
    mutation: (config: OverlayConfig) => OverlayConfig,
  ): Promise<OverlayConfig> {
    const reference = doc(db, "overlayConfigs", overlayID);
    return runTransaction(db, async (transaction) => {
      const snapshot = await transaction.get(reference);
      const current = snapshot.exists() ? parseOverlayConfig(snapshot.id, snapshot.data()) : null;
      if (!current) throw new OverlayApplicationError("overlayUnavailable");
      if (current.leagueID !== leagueID) throw new OverlayApplicationError("workspaceMismatch");
      if (expectedFingerprint && await currentFingerprint(current) !== expectedFingerprint) throw new OverlayApplicationError("staleApproval");
      const next = mutation(current);
      transaction.update(reference, { elements: overlayElementsFirestoreData(next.elements), skinContractWrittenAt: serverTimestamp(), updatedAt: serverTimestamp() });
      return { ...next, updatedAt: new Date() };
    });
  }

  private async loadSources(leagueID: string): Promise<LeagueExternalDataSource[]> {
    const snapshots = await getDocs(query(collection(db, "leagueExternalDataSources"), where("leagueID", "==", leagueID)));
    return snapshots.docs
      .map((entry) => parseExternalDataSource(entry.id, entry.data()))
      .filter((source): source is LeagueExternalDataSource => source?.status === "active" && source.entityKind === "athletes");
  }

  private async cachedPersonOptions(overlayID: string, source: LeagueExternalDataSource, maximum: number, requireReady: boolean) {
    const cache = await getDoc(doc(db, "overlayPersonWorkspaces", overlayID, "sourceCaches", source.id));
    const data = cache.data();
    const generationID = typeof data?.activeGenerationID === "string" ? data.activeGenerationID : undefined;
    if (data?.status !== "ready" || !generationID) {
      if (requireReady) {
        throw new OverlayApplicationError(
          "invalidPatch",
          "The selected source is not currently ready. Open Broadcast Overlays to load or authorize private source data, then retry.",
        );
      }
      return [];
    }
    const records = await getDocs(query(
      collection(db, "overlayPersonWorkspaces", overlayID, "sourceCaches", source.id, "generations", generationID, "records"),
      orderBy("index"),
      queryLimit(maximum),
    ));
    const values = records.docs.flatMap((entry) => {
      const json = entry.data().json;
      if (typeof json !== "string") return [];
      try {
        const value = JSON.parse(json) as unknown;
        return value && typeof value === "object" && !Array.isArray(value) ? [value as Record<string, unknown>] : [];
      } catch {
        return [];
      }
    });
    return personOptionsFromExternalRecords(values, source, await fetchCountryResources().catch(() => []));
  }

  private async resolveCandidateHandles(
    overlayID: string,
    leagueID: string,
    control: OverlayInputControl,
    controlPatch: Record<string, unknown>,
    dataPatch: Record<string, unknown>,
    config: OverlayConfig,
  ): Promise<OverlayResolvedInputPatches> {
    const resolvedControl = structuredClone(controlPatch);
    const resolvedData = structuredClone(dataPatch);
    if (Object.hasOwn(resolvedControl, "skin") && !Object.hasOwn(resolvedControl, "customSkin")) resolvedControl.customSkin = null;
    if (resolvedControl.customSkin != null) {
      const ref = parseCustomSkinReference(resolvedControl.customSkin);
      if (!ref) throw new OverlayApplicationError("invalidPatch", "Invalid custom skin reference.");
      await readSkinRevision(leagueID, ref);
    }
    if (typeof resolvedData.sourceHandle === "string") {
      const sourceID = parseCandidateHandle(resolvedData.sourceHandle, "source");
      if (sourceID !== LEAGUE_ATHLETES_SOURCE_ID && !(await this.loadSources(leagueID)).some((source) => source.id === sourceID)) {
        throw new OverlayApplicationError("invalidPatch", "Source handle is no longer available.");
      }
      delete resolvedData.sourceHandle;
      resolvedData.sourceID = sourceID;
    }
    if (typeof resolvedData.personHandle === "string") {
      const match = /^person:([^:]+):(.+)$/.exec(resolvedData.personHandle);
      if (!match) throw new OverlayApplicationError("invalidPatch", "Person handle is invalid.");
      const [, sourceID, recordKey] = match;
      let person: OverlayLowerThirdPerson | undefined;
      if (sourceID === LEAGUE_ATHLETES_SOURCE_ID) {
        const athleteSnapshot = await getDoc(doc(db, "leagueAthleteValues", recordKey));
        const athlete = athleteSnapshot.exists() ? parseAthleteValue(athleteSnapshot.id, athleteSnapshot.data()) : null;
        if (!athlete || athlete.leagueID !== leagueID || athlete.status !== "active") {
          throw new OverlayApplicationError("invalidPatch", "Person handle is no longer available.");
        }
        // Same snapshot builder as the designer picker, so broadcast/short names resolve identically.
        const schemaSnapshot = await getDoc(doc(db, "leagueAthleteSchemas", leagueID));
        const schema = schemaSnapshot.exists() ? parseFieldSchema(schemaSnapshot.id, schemaSnapshot.data()) : null;
        person = personOptionsFromAthletes([athlete], schema)[0]?.person ?? { "full-name": athlete.name, "short-name": shortNameFromFullName(athlete.name) };
      } else {
        const source = (await this.loadSources(leagueID)).find((candidate) => candidate.id === sourceID);
        if (!source) throw new OverlayApplicationError("invalidPatch", "Person source is no longer available.");
        person = (await this.cachedPersonOptions(overlayID, source, 1_000, true)).find((option) => option.recordKey === recordKey)?.person;
        if (!person) throw new OverlayApplicationError("invalidPatch", "Person handle is no longer available.");
      }
      delete resolvedData.personHandle;
      resolvedData.sourceID = sourceID;
      resolvedData.recordKey = recordKey;
      resolvedData.person = person;
    }
    if (typeof resolvedData.gpsDeviceHandle === "string") {
      if (control.type !== "miniMap" && control.type !== "fullMap" && control.type !== "speedometer" && control.type !== "gradeScope" && control.type !== "compass") throw new OverlayApplicationError("invalidPatch", "gpsDeviceHandle is a top-level field only for mini-map, full-screen-map, speedometer, grade-scope, and compass inputs.");
      resolvedData.deviceID = await this.validatedDevice(parseCandidateHandle(resolvedData.gpsDeviceHandle, "gpsDevice"), config.competitionInstanceID);
      delete resolvedData.gpsDeviceHandle;
    }
    if (Array.isArray(resolvedData.tracks)) {
      resolvedData.tracks = await Promise.all(resolvedData.tracks.map(async (rawTrack) => {
        if (!rawTrack || typeof rawTrack !== "object" || Array.isArray(rawTrack)) return rawTrack;
        const track = { ...(rawTrack as Record<string, unknown>) };
        if (typeof track.gpsDeviceHandle === "string") {
          track.deviceID = await this.validatedDevice(parseCandidateHandle(track.gpsDeviceHandle, "gpsDevice"), config.competitionInstanceID);
          delete track.gpsDeviceHandle;
        }
        return track;
      }));
    }
    if (typeof resolvedControl.organizationHandle === "string") {
      const organizationID = parseCandidateHandle(resolvedControl.organizationHandle, "organization");
      const snapshot = await getDoc(doc(db, "organizations", organizationID));
      const organization = snapshot.exists() ? parseOrganization(snapshot.id, snapshot.data()) : null;
      if (!organization || organization.leagueID !== leagueID) throw new OverlayApplicationError("invalidPatch", "Organization handle is no longer available.");
      delete resolvedControl.organizationHandle;
      let applied = false;
      for (const key of ["team1Color", "team2Color", "ringColor", "baseColor", "tintColor", "courseLineColor", "background"]) {
        const color = resolvedControl[key];
        if (color && typeof color === "object" && !Array.isArray(color)) {
          const record = { ...(color as Record<string, unknown>) };
          if (record.source === "org-primary" || record.source === "org-secondary") {
            record.organizationID = organizationID;
            resolvedControl[key] = record;
            applied = true;
          }
        }
      }
      if (!applied) throw new OverlayApplicationError("invalidPatch", "organizationHandle requires an organization color field in the same control patch.");
    }
    return { control: resolvedControl, data: resolvedData };
  }

  private async validatedDevice(deviceID: string, competitionInstanceID: string): Promise<string> {
    const snapshot = await getDoc(doc(db, "leagueCompetitionInstances", competitionInstanceID, "gpsData", deviceID));
    const device = snapshot.exists() ? parseTrackerDocument(snapshot.id, snapshot.data()) : null;
    if (!device || device.competitionInstanceID !== competitionInstanceID) {
      throw new OverlayApplicationError("invalidPatch", "GPS device handle is no longer available.");
    }
    return deviceID;
  }
}

function guide(control: string[], data: string[]): OverlayInputFieldGuide {
  return { control: [...new Set(control)].sort(), data: [...new Set(data)].sort() };
}

function selectedValues(record: Record<string, unknown>, fields: string[]): Record<string, unknown> {
  return Object.fromEntries(fields.flatMap((field) => record[field] === undefined ? [] : [[field, jsonSafe(record[field])]]));
}

function jsonSafe(value: unknown): unknown {
  if (value instanceof Date) return value;
  if (Array.isArray(value)) return value.map(jsonSafe);
  if (value && typeof value === "object") {
    return Object.fromEntries(Object.entries(value as Record<string, unknown>).flatMap(([key, child]) => child === undefined ? [] : [[key, jsonSafe(child)]]));
  }
  return value;
}

function canonical(value: unknown): unknown {
  if (value instanceof Date) return value.getTime();
  if (Array.isArray(value)) return value.map(canonical);
  if (value && typeof value === "object") {
    return Object.fromEntries(Object.entries(value as Record<string, unknown>).sort(([first], [second]) => first.localeCompare(second)).map(([key, child]) => [key, canonical(child)]));
  }
  return value ?? null;
}

async function fingerprint(value: unknown): Promise<string> {
  const bytes = new TextEncoder().encode(JSON.stringify(canonical(value)));
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

/**
 * Strict AI-write gate for custom widgets: the public-page parser is tolerant
 * (clamp/drop/fallback), but a tool write whose spec would lose layers or
 * controls in sanitization must fail loudly so the model can correct itself —
 * never silently persist a stripped-down widget. deepMerge replaces arrays
 * wholesale, so a spec patch is always a complete spec.
 */
function validateCustomWidgetPatch(dataPatch: Record<string, unknown>): void {
  if (dataPatch.spec === undefined) return;
  const issues = customWidgetSpecIssues(dataPatch.spec);
  if (issues.length > 0) {
    throw new OverlayApplicationError("invalidPatch", `Invalid custom widget spec: ${issues.join("; ")}.`);
  }
}

/** Value patches must target declared controls — unknown keys are an error, not a silent drop. */
function validateCustomWidgetValueKeys(values: unknown, parsed: OverlayCustomWidgetInstance): void {
  if (!values || typeof values !== "object" || Array.isArray(values)) {
    throw new OverlayApplicationError("invalidPatch", "Custom widget values must be an object of controlKey → value.");
  }
  const declared = new Set(parsed.spec.controls.map((control) => control.key));
  const unknown = Object.keys(values).filter((key) => !declared.has(key)).sort();
  if (unknown.length > 0) {
    throw new OverlayApplicationError(
      "invalidPatch",
      `Unknown custom widget control key${unknown.length === 1 ? "" : "s"}: ${unknown.join(", ")}. `
      + `Declared keys: ${[...declared].sort().join(", ") || "(none)"}.`,
    );
  }
}

function validatePatch(patch: Record<string, unknown>, allowed: string[], namespace: string): void {
  const unknown = Object.keys(patch).filter((key) => !allowed.includes(key)).sort();
  if (unknown.length > 0) throw new OverlayApplicationError("invalidPatch", `Unsupported ${namespace} field${unknown.length === 1 ? "" : "s"}: ${unknown.join(", ")}.`);
  try {
    JSON.stringify(patch);
  } catch {
    throw new OverlayApplicationError("invalidPatch", `${namespace[0].toUpperCase()}${namespace.slice(1)} values must be valid JSON.`);
  }
}

function deepMerge(base: Record<string, unknown>, patch: Record<string, unknown>): Record<string, unknown> {
  const result = { ...base };
  for (const [key, value] of Object.entries(patch)) {
    const current = result[key];
    // Dates are leaf values (advanced timer targetAt), never merged key-wise.
    result[key] = value && typeof value === "object" && !Array.isArray(value) && !(value instanceof Date)
      && current && typeof current === "object" && !Array.isArray(current) && !(current instanceof Date)
      ? deepMerge(current as Record<string, unknown>, value as Record<string, unknown>)
      : value;
  }
  return result;
}

function elementList(config: OverlayConfig, type: OverlayElementType): OverlayElementInstance[] {
  return config.elements[OVERLAY_ELEMENT_TYPE_KEYS[type]] as OverlayElementInstance[];
}

function withElements(config: OverlayConfig, type: OverlayElementType, instances: OverlayElementInstance[]): OverlayConfig {
  return { ...config, elements: { ...config.elements, [OVERLAY_ELEMENT_TYPE_KEYS[type]]: instances } as OverlayElements };
}

function instanceForControl(config: OverlayConfig, control: OverlayInputControl): OverlayElementInstance | undefined {
  return elementList(config, control.type).find((instance) => instance.id === control.instanceID);
}

/** Parses the AI's ISO-8601 targetAt into a Date and checks the IANA zone. */
function advancedTimerControlPatch(controlPatch: Record<string, unknown>): Record<string, unknown> {
  const next = { ...controlPatch };
  if (Object.hasOwn(next, "targetAt") && next.targetAt !== null) {
    const parsed = typeof next.targetAt === "string" ? new Date(next.targetAt) : null;
    if (!parsed || !Number.isFinite(parsed.getTime())) {
      throw new OverlayApplicationError("invalidPatch", "Advanced timer targetAt must be an ISO-8601 date-time with an offset, e.g. 2026-10-10T07:00:00-10:00.");
    }
    next.targetAt = parsed;
  }
  if (Object.hasOwn(next, "timeZone") && !isValidTimeZone(next.timeZone)) {
    throw new OverlayApplicationError("invalidPatch", "Advanced timer timeZone must be an IANA time zone such as Pacific/Honolulu.");
  }
  if (Object.hasOwn(next, "mode") && next.mode !== "countdown" && next.mode !== "countup") {
    throw new OverlayApplicationError("invalidPatch", "Advanced timer mode must be countdown or countup.");
  }
  return next;
}

function validateTickerMessagesPatch(messages: unknown): void {
  const valid = Array.isArray(messages)
    && messages.length <= TICKER_MESSAGE_LIMIT
    && messages.every((message) => !!message && typeof message === "object"
      && typeof (message as Record<string, unknown>).id === "string" && !!((message as Record<string, unknown>).id as string).trim()
      && typeof (message as Record<string, unknown>).text === "string"
      && ((message as Record<string, unknown>).text as string).length <= TICKER_MESSAGE_MAX_LENGTH);
  if (!valid) {
    throw new OverlayApplicationError("invalidPatch", `Ticker messages must be a list of at most ${TICKER_MESSAGE_LIMIT} { id, text } entries with text up to ${TICKER_MESSAGE_MAX_LENGTH} characters.`);
  }
}

function replaceInstance(config: OverlayConfig, control: OverlayInputControl, mutation: (instance: OverlayElementInstance) => OverlayElementInstance): OverlayConfig {
  const instances = elementList(config, control.type);
  if (!instances.some((instance) => instance.id === control.instanceID)) throw new OverlayApplicationError("instanceUnavailable");
  return withElements(config, control.type, instances.map((instance) => instance.id === control.instanceID ? mutation(instance) : instance));
}

function instanceFirestoreData(type: OverlayElementType, instance: OverlayElementInstance): Record<string, unknown> {
  switch (type) {
    case "gps": return gpsGroupInstanceFirestoreData(instance as Parameters<typeof gpsGroupInstanceFirestoreData>[0]);
    case "timer": return timerInstanceFirestoreData(instance as Parameters<typeof timerInstanceFirestoreData>[0]);
    case "pylon": return pylonInstanceFirestoreData(instance as Parameters<typeof pylonInstanceFirestoreData>[0]);
    case "gpsPylon": return gpsPylonInstanceFirestoreData(instance as Parameters<typeof gpsPylonInstanceFirestoreData>[0]);
    case "sortFilterPylon": return sortFilterPylonInstanceFirestoreData(instance as Parameters<typeof sortFilterPylonInstanceFirestoreData>[0]);
    case "lowerThird": return lowerThirdInstanceFirestoreData(instance as Parameters<typeof lowerThirdInstanceFirestoreData>[0]);
    case "customLowerThird": return customLowerThirdInstanceFirestoreData(instance as Parameters<typeof customLowerThirdInstanceFirestoreData>[0]);
    case "playerCard": return playerCardInstanceFirestoreData(instance as Parameters<typeof playerCardInstanceFirestoreData>[0]);
    case "scoreboard": return scoreboardInstanceFirestoreData(instance as Parameters<typeof scoreboardInstanceFirestoreData>[0]);
    case "miniMap": return miniMapInstanceFirestoreData(instance as Parameters<typeof miniMapInstanceFirestoreData>[0]);
    case "fullMap": return fullMapInstanceFirestoreData(instance as Parameters<typeof fullMapInstanceFirestoreData>[0]);
    case "courseProfile": return courseProfileInstanceFirestoreData(instance as Parameters<typeof courseProfileInstanceFirestoreData>[0]);
    case "speedometer": return speedometerInstanceFirestoreData(instance as Parameters<typeof speedometerInstanceFirestoreData>[0]);
    case "gradeScope": return gradeScopeInstanceFirestoreData(instance as Parameters<typeof gradeScopeInstanceFirestoreData>[0]);
    case "customWidget": return customWidgetInstanceFirestoreData(instance as Parameters<typeof customWidgetInstanceFirestoreData>[0]);
    case "bug": return bugInstanceFirestoreData(instance as Parameters<typeof bugInstanceFirestoreData>[0]);
    case "compass": return compassInstanceFirestoreData(instance as Parameters<typeof compassInstanceFirestoreData>[0]);
    case "billboard": return billboardInstanceFirestoreData(instance as Parameters<typeof billboardInstanceFirestoreData>[0]);
    case "podium": return podiumInstanceFirestoreData(instance as Parameters<typeof podiumInstanceFirestoreData>[0]);
    case "webSource": return webSourceInstanceFirestoreData(instance as Parameters<typeof webSourceInstanceFirestoreData>[0]);
    case "imageCanvas": return imageCanvasInstanceFirestoreData(instance as Parameters<typeof imageCanvasInstanceFirestoreData>[0]);
    case "startList": return startListInstanceFirestoreData(instance as Parameters<typeof startListInstanceFirestoreData>[0]);
    case "ticker": return tickerInstanceFirestoreData(instance as OverlayTickerInstance);
    case "advancedTimer": return advancedTimerInstanceFirestoreData(instance as OverlayAdvancedTimerInstance);
  }
}

function parseInstance(type: OverlayElementType, raw: Record<string, unknown>): OverlayElementInstance {
  const parsed = parseOverlayElements({ elements: { [OVERLAY_ELEMENT_TYPE_KEYS[type]]: [raw] } });
  const instance = (parsed[OVERLAY_ELEMENT_TYPE_KEYS[type]] as OverlayElementInstance[]).find((candidate) => candidate.id === raw.id);
  if (!instance) throw new OverlayApplicationError("invalidPatch", `Invalid ${type} input values.`);
  return instance;
}

function newInstance(type: OverlayElementType, id: string, name: string): OverlayElementInstance {
  switch (type) {
    case "gps": return newGpsGroupInstance(id, name);
    case "timer": return newTimerInstance(id, name);
    case "pylon": return newPylonInstance(id, name);
    case "gpsPylon": return newGpsPylonInstance(id, name);
    case "sortFilterPylon": return newSortFilterPylonInstance(id, name);
    case "lowerThird": return newLowerThirdInstance(id, name);
    case "customLowerThird": return newCustomLowerThirdInstance(id, name);
    case "playerCard": return newPlayerCardInstance(id, name);
    case "scoreboard": return newScoreboardInstance(id, name);
    case "miniMap": return newMiniMapInstance(id, name);
    case "fullMap": return newFullMapInstance(id, name);
    case "courseProfile": return newCourseProfileInstance(id, name);
    case "speedometer": return newSpeedometerInstance(id, name);
    case "gradeScope": return newGradeScopeInstance(id, name);
    case "customWidget": return newCustomWidgetInstance(id, name);
    case "bug": return newBugInstance(id, name);
    case "compass": return newCompassInstance(id, name);
    case "billboard": return newBillboardInstance(id, name);
    case "podium": return newPodiumInstance(id, name);
    case "webSource": return newWebSourceInstance(id, name);
    case "imageCanvas": return newImageCanvasInstance(id, name);
    case "startList": return newStartListInstance(id, name);
    case "ticker": return newTickerInstance(id, name);
    case "advancedTimer": return newAdvancedTimerInstance(id, name);
  }
}

function inputTypeLabel(type: OverlayElementType): string {
  switch (type) {
    case "gps": return "GPS";
    case "timer": return "Timer";
    case "pylon": return "Pylon";
    case "gpsPylon": return "GPS Pylon";
    case "sortFilterPylon": return "Sort & Filter Pylon";
    case "lowerThird": return "Lower Third";
    case "customLowerThird": return "Custom Lower Third";
    case "playerCard": return "Player Card";
    case "scoreboard": return "Scoreboard";
    case "miniMap": return "Mini-map";
    case "fullMap": return "Full-screen Map";
    case "courseProfile": return "Course Profile";
    case "speedometer": return "Speedometer";
    case "gradeScope": return "Grade Scope";
    case "customWidget": return "Custom Widget";
    case "bug": return "Bug";
    case "compass": return "Compass";
    case "billboard": return "Billboard";
    case "podium": return "Podium";
    case "imageCanvas": return "Image Canvas";
    case "webSource": return "Web Source";
    case "startList": return "Start List";
    case "ticker": return "Ticker";
    case "advancedTimer": return "Advanced Timer";
  }
}

function transitionTimer(timer: OverlayTimerInstance, action: "start" | "pause" | "reset", now: Date): OverlayTimerInstance {
  if (action === "start") return timer.running ? timer : { ...timer, running: true, startedAt: now };
  if (action === "pause") return timer.running
    ? { ...timer, running: false, startedAt: null, offsetMs: timerElapsedMs(timer, now.getTime()) }
    : timer;
  return { ...timer, running: false, startedAt: null, offsetMs: 0 };
}

function applyingRuntimeControls(config: OverlayConfig, runtime: OverlayRuntimeState): OverlayConfig {
  // Raw controls: container-gated values would erase each instance's own flag.
  const controls = runtime.rawControls ?? runtime.controls;
  let next = config;
  for (const control of OverlayInputParameterCodec.controls(config)) {
    const key = overlayControlKey(control.type, control.instanceID);
    if (key in controls) next = replaceInstance(next, control, (instance) => ({ ...instance, visible: controls[key] } as OverlayElementInstance));
  }
  return next;
}

function parseCandidateHandle(value: string, prefix: string): string {
  const expected = `${prefix}:`;
  if (!value.startsWith(expected) || value.length === expected.length) throw new OverlayApplicationError("invalidPatch", "Candidate handle is invalid.");
  return value.slice(expected.length);
}

function personWorkspaceDataKeys(control: OverlayInputControl, data: Record<string, unknown>): Set<string> {
  const keys = control.type === "lowerThird" || control.type === "playerCard"
    ? ["sourceID", "recordKey", "person"]
    : control.type === "pylon" || control.type === "gpsPylon" || control.type === "sortFilterPylon" || control.type === "podium" || control.type === "startList" ? ["sourceID"] : [];
  return new Set(keys.filter((key) => Object.hasOwn(data, key)));
}

function personWorkspaceSlotFor(type: "lowerThird" | "playerCard", instanceID: string): string {
  return type === "playerCard" ? playerCardWorkspaceSlot(instanceID) : lowerThirdWorkspaceSlot(instanceID);
}

async function routePersonWorkspaceChanges(overlayID: string, control: OverlayInputControl, data: Record<string, unknown>): Promise<void> {
  if (control.type === "lowerThird" || control.type === "playerCard") {
    if (!["sourceID", "recordKey", "person"].some((key) => Object.hasOwn(data, key))) return;
    const slot = personWorkspaceSlotFor(control.type, control.instanceID);
    const sourceID = typeof data.sourceID === "string" ? data.sourceID : undefined;
    if (Object.hasOwn(data, "recordKey") || Object.hasOwn(data, "person")) {
      const recordKey = typeof data.recordKey === "string" ? data.recordKey : undefined;
      const person = data.person && typeof data.person === "object" && !Array.isArray(data.person)
        ? data.person as unknown as OverlayLowerThirdPerson
        : undefined;
      if (!sourceID || !recordKey || !person?.["full-name"]?.trim()) {
        throw new OverlayApplicationError("invalidPatch", "Use a validated personHandle, or provide sourceID, recordKey, and person together.");
      }
      await selectOverlayPerson(overlayID, slot, sourceID, recordKey, person);
    } else {
      await selectOverlayPersonSource(overlayID, slot, sourceID);
    }
  } else if ((control.type === "pylon" || control.type === "gpsPylon" || control.type === "sortFilterPylon" || control.type === "podium" || control.type === "startList") && Object.hasOwn(data, "sourceID")) {
    await selectOverlayPersonSource(
      overlayID,
      control.type === "podium"
        ? podiumWorkspaceSlot(control.instanceID)
        : control.type === "startList"
          ? startListWorkspaceSlot(control.instanceID)
        : control.type === "gpsPylon"
          ? gpsPylonWorkspaceSlot(control.instanceID)
          : control.type === "sortFilterPylon"
            ? sortFilterPylonWorkspaceSlot(control.instanceID)
            : pylonWorkspaceSlot(control.instanceID),
      typeof data.sourceID === "string" ? data.sourceID : undefined,
    );
  }
}

export const OverlayInputLimits = {
  pylonRows: PYLON_ROW_LIMIT,
  sortFilterPylonRows: SORT_FILTER_PYLON_ROW_LIMIT,
  startListRows: START_LIST_ROW_LIMIT,
  pylonDividers: PYLON_DIVIDER_LIMIT,
  podiumSlots: PODIUM_SLOT_LIMIT,
  customRows: CUSTOM_LOWER_THIRD_ROW_LIMIT,
  courseProfileTracks: COURSE_PROFILE_TRACK_LIMIT,
};
